import { auth } from './firebase';

let quotaRetryAt = 0;
let quotaMessage = '';
const pendingKey = user => `booking-pending-v1:${user.uid}`;
const uncertainCodes = new Set(['booking/unavailable', 'booking/internal', 'booking/resource-exhausted', 'booking/aborted', 'booking/unauthenticated']);

export function getPendingBooking() {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const value = JSON.parse(window.localStorage.getItem(pendingKey(user)) || 'null');
    return value?.requestId && Array.isArray(value.data?.slots) ? value : null;
  } catch { return null; }
}
function clearPending(user, requestId) {
  try {
    const value = JSON.parse(window.localStorage.getItem(pendingKey(user)) || 'null');
    if (value?.requestId === requestId) window.localStorage.removeItem(pendingKey(user));
  } catch { /* A saved booking remains authoritative even if browser storage fails. */ }
}
function confirmedBooking(result, expectedCount) {
  if (!Array.isArray(result?.lessonIds) || result.lessonIds.length !== expectedCount ||
      result.lessonIds.some(id => typeof id !== 'string' || !id) || new Set(result.lessonIds).size !== expectedCount)
    throw Object.assign(Error('Ответ о сохранении брони не получен. Проверьте результат перед новой попыткой.'), { code: 'booking/unavailable' });
  return result;
}

async function call(route, data) {
  const user = auth.currentUser;
  if (!user) throw Object.assign(Error('Войдите в аккаунт.'), { code: 'booking/unauthenticated' });
  if (Date.now() < quotaRetryAt)
    throw Object.assign(Error(quotaMessage), { code: 'booking/resource-exhausted' });
  quotaRetryAt = 0;
  const token = await user.getIdToken();
  if (user !== auth.currentUser) throw Object.assign(Error('Аккаунт изменился. Обновите страницу.'), { code: 'booking/unauthenticated' });
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 45000);
  let response;
  try {
    response = await fetch(`/booking-api/index.php?route=${route}`, {
      method: 'POST', credentials: 'omit', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  } catch {
    throw Object.assign(Error(controller.signal.aborted
      ? 'Сервер календаря не ответил за 45 секунд. Повторите попытку.'
      : 'Не удалось связаться с календарём. Проверьте соединение и повторите попытку.'), { code: 'booking/unavailable' });
  } finally { window.clearTimeout(timer); }
  let result;
  try { result = await response.json(); }
  catch { throw Object.assign(Error('Сервер календаря вернул некорректный ответ. Повторите попытку.'), { code: 'booking/unavailable', status: response.status }); }
  if (!result || typeof result !== 'object' || Array.isArray(result))
    throw Object.assign(Error('Подтверждение сохранения не получено. Повторите проверку.'), { code: 'booking/unavailable' });
  if (result.error?.code === 'resource-exhausted') {
    quotaRetryAt = Date.now() + 60000;
    quotaMessage = result.error.message;
  }
  if (!response.ok || result.error) throw Object.assign(
    Error(result.error?.message || 'Не удалось выполнить действие. Повторите попытку.'),
    { code: `booking/${result.error?.code || 'unavailable'}`, status: response.status },
  );
  if (user !== auth.currentUser) throw Object.assign(Error('Аккаунт изменился. Обновите страницу.'), { code: 'booking/unauthenticated' });
  if (!result.data || typeof result.data !== 'object')
    throw Object.assign(Error('Не удалось получить результат. Повторите проверку.'), { code: 'booking/unavailable' });
  return result.data;
}
export const newRequestId = () => window.crypto.randomUUID();
export async function bookingAction(action, data = {}, requestId = newRequestId()) {
  if (action !== 'book') return call('action', { ...data, action, requestId });
  const user = auth.currentUser;
  if (!user) return call('action', { ...data, action, requestId });
  const old = getPendingBooking();
  if (old && JSON.stringify(old.data.slots) !== JSON.stringify(data.slots))
    throw Object.assign(Error('Сначала проверьте результат предыдущего бронирования.'), { code: 'booking/pending' });
  const pending = old || { requestId, data, createdAt: Date.now() };
  // Persist the exact payload BEFORE sending it. Retries after a reload must
  // match the server fingerprint, even if the name or timezone has changed.
  try { window.localStorage.setItem(pendingKey(user), JSON.stringify(pending)); }
  catch { throw Object.assign(Error('Браузер не может сохранить запрос. Разрешите хранение данных сайта и повторите.'), { code: 'booking/storage-unavailable' }); }
  try {
    const result = confirmedBooking(await call('action', { ...pending.data, action, requestId: pending.requestId }), pending.data.slots.length);
    clearPending(user, pending.requestId);
    return result;
  } catch (error) {
    if (!uncertainCodes.has(error.code)) clearPending(user, pending.requestId);
    throw error;
  }
}
export async function checkPendingBooking() {
  const user = auth.currentUser, pending = getPendingBooking();
  if (!pending) return null;
  const status = await call('action', { action: 'bookingStatus', requestId: pending.requestId });
  if (!status.found) return null;
  const result = confirmedBooking(status.result, pending.data.slots.length);
  clearPending(user, pending.requestId);
  return result;
}
export async function retryPendingBooking() {
  const pending = getPendingBooking();
  if (!pending) return null;
  return bookingAction('book', pending.data, pending.requestId);
}
export async function loadBookingSchedule() { return call('schedule', {}); }
